import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { request } from "../../../API/index";

const CreatePhotoPage = () => {
  const navigate = useNavigate();
  const mainFileRef = useRef(null);
  const galleryFileRef = useRef(null);

  // ✅ 날짜 수정 (배열 → 문자열)
  const [formData, setFormData] = useState({
    title: "",
    date: new Date().toISOString().split("T")[0],
    desc: "",
  });

  const [mainImgPreview, setMainImgPreview] = useState(null);
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [mainFile, setMainFile] = useState(null);

  // -------------------------------
  // 텍스트 입력
  // -------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // -------------------------------
  // 대표 이미지
  // -------------------------------
  const changeMainImg = (file) => {
    if (!file) return;

    if (mainImgPreview) {
      URL.revokeObjectURL(mainImgPreview);
    }

    setMainFile(file); // ✅ 파일 저장
    setMainImgPreview(URL.createObjectURL(file));
  };

  const removeMainImg = () => {
    if (mainImgPreview) URL.revokeObjectURL(mainImgPreview);
    setMainImgPreview(null);
    if (mainFileRef.current) mainFileRef.current.value = "";
  };

  // -------------------------------
  // 갤러리 이미지
  // -------------------------------
  const addGalleryFiles = (files) => {
    const newItems = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9),
    }));

    setNewGalleryFiles((prev) => [...prev, ...newItems]);

    // 같은 파일 다시 선택 가능하게
    if (galleryFileRef.current) galleryFileRef.current.value = "";
  };

  const removeNewFile = (id) => {
    setNewGalleryFiles((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((item) => item.id !== id);
    });
  };

  // -------------------------------
  // 메모리 정리 (중요)
  // -------------------------------
  useEffect(() => {
    return () => {
      if (mainImgPreview) URL.revokeObjectURL(mainImgPreview);

      newGalleryFiles.forEach((item) => {
        URL.revokeObjectURL(item.preview);
      });
    };
  }, []);

  // -------------------------------
  // 서버 전송
  // -------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ 검증 강화
    if (!formData.title.trim()) {
      return alert("제목을 입력해 주세요.");
    }

    if (!mainFile) {
      return alert("대표 사진을 등록해 주세요.");
    }

    const formDataToSend = new FormData();

    formDataToSend.append("title", formData.title);
    formDataToSend.append("date", formData.date);
    formDataToSend.append("desc", formData.desc);

    formDataToSend.append("newMain", mainFile);

    newGalleryFiles.forEach((item) => {
      formDataToSend.append("newGallery", item.file);
    });

    try {
      const response = await request.post(
        `/Dashboard/createImages`,
        formDataToSend,
      );

      if (response.status === 200) {
        alert("새 콘텐츠가 성공적으로 등록되었습니다.");
        navigate(-1);
      } else {
        alert(response.data?.message || "등록 실패");
      }
    } catch (error) {
      console.error("API 전송 에러:", error);
      alert("서버와 통신 중 에러가 발생했습니다.");
    }
  };

  return (
    <Container>
      <Header>
        <button onClick={() => navigate(-1)}>← 뒤로가기</button>
        <h1>새 게시물 등록</h1>
      </Header>

      <FormCard onSubmit={handleSubmit}>
        <InputGroup>
          <label>게시물 제목</label>
          <input
            name="title"
            value={formData.title}
            onChange={handleInputChange}
          />
        </InputGroup>

        <InputGroup>
          <label>상세 설명</label>
          <textarea
            name="desc"
            rows="4"
            value={formData.desc}
            onChange={handleInputChange}
          />
        </InputGroup>

        {/* 대표 이미지 */}
        <PhotoSection>
          <label>대표 사진</label>
          <MainPhotoCard>
            {mainImgPreview ? (
              <>
                <img src={mainImgPreview} alt="Preview" />
                <PhotoActions>
                  <ActionButton
                    type="button"
                    onClick={() => mainFileRef.current.click()}
                  >
                    변경
                  </ActionButton>
                  <ActionButton
                    type="button"
                    className="delete"
                    onClick={removeMainImg}
                  >
                    삭제
                  </ActionButton>
                </PhotoActions>
              </>
            ) : (
              <EmptyPhoto onClick={() => mainFileRef.current.click()}>
                + 사진 등록
              </EmptyPhoto>
            )}
            <input
              type="file"
              ref={mainFileRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0]; // ✅ 핵심
                if (file) changeMainImg(file);
                e.target.value = "";
              }}
            />
          </MainPhotoCard>
        </PhotoSection>

        {/* 갤러리 */}
        <PhotoSection>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <label>갤러리 ({newGalleryFiles.length})</label>
            <AddBtn
              type="button"
              onClick={() => galleryFileRef.current.click()}
            >
              + 사진 추가
            </AddBtn>

            <input
              type="file"
              ref={galleryFileRef}
              style={{ display: "none" }}
              multiple
              accept="image/*"
              onChange={(e) => {
                if (e.target.files.length > 0) {
                  addGalleryFiles(Array.from(e.target.files));
                }
              }}
            />
          </div>

          <GalleryGrid>
            {newGalleryFiles.map((item) => (
              <GalleryCard key={item.id}>
                <img src={item.preview} alt="preview" />
                <DeleteBadge onClick={() => removeNewFile(item.id)}>
                  ×
                </DeleteBadge>
              </GalleryCard>
            ))}
          </GalleryGrid>
        </PhotoSection>

        <ButtonGroup>
          <CancelBtn type="button" onClick={() => navigate(-1)}>
            취소
          </CancelBtn>
          <SaveBtn type="submit">등록</SaveBtn>
        </ButtonGroup>
      </FormCard>
    </Container>
  );
};

export default CreatePhotoPage;

/* --- Styled Components --- */
const Container = styled.div`
  padding: 3rem;
  max-width: 900px;
  margin: 0 auto;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  h1 {
    font-size: 1.6rem;
    font-weight: 700;
  }
  button {
    background: none;
    border: none;
    cursor: pointer;
    color: #64748b;
  }
`;
const FormCard = styled.form`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
`;
const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #334155;
  }
  input,
  textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
  }
`;
const PhotoSection = styled.div`
  margin-bottom: 2.5rem;
`;
const MainPhotoCard = styled.div`
  width: 300px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const PhotoActions = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.6);
  padding: 5px;
  text-align: center;
`;
const ActionButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  &.delete {
    color: #ff4d4f;
    margin-left: 10px;
  }
`;
const EmptyPhoto = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
`;
const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 15px;
  border: 1px solid #e2e8f0;
`;
const GalleryCard = styled.div`
  position: relative;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
  border: ${(props) =>
    props.isNew ? "2px solid #2563eb" : "1px solid #cbd5e1"};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const NewLabel = styled.span`
  position: absolute;
  bottom: 5px;
  left: 5px;
  background: #2563eb;
  color: white;
  font-size: 10px;
  padding: 2px 5px;
  border-radius: 4px;
  font-weight: 800;
`;
const DeleteBadge = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  background: #ef4444;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
`;
const AddBtn = styled.button`
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
`;
const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;
const SaveBtn = styled.button`
  flex: 2;
  padding: 1rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
`;
const CancelBtn = styled.button`
  flex: 1;
  padding: 1rem;
  background: #f1f5f9;
  border-radius: 12px;
  border: none;
  cursor: pointer;
`;
const EmptyGrid = styled.div`
  grid-column: 1/-1;
  text-align: center;
  color: #94a3b8;
  padding: 2rem;
`;
